/** The keys a number question can carry for validation. Loose on purpose: callers hold questions
 * from the old builder shape, where everything is optional and untyped. */
export interface NumberValidationKeys {
  numberValidation?: unknown;
  numberValidationRelative?: unknown;
  validationOffset?: unknown;
  lowerValue?: unknown;
  upperValue?: unknown;
  lowerMargin?: unknown;
  upperMargin?: unknown;
  [key: string]: unknown;
}

/**
 * Applies a validation change to a question and drops the keys the new state cannot use.
 *
 * This is the logic that can destroy a saved configuration, so it is a pure function with its own
 * tests rather than two copies inlined in the top-level and table handlers of `formBuilder`. The
 * copies were the risk: the table one silently lacked the mutual-exclusion deletes for a while,
 * so the same edit left different keys behind depending on where the question lived, and nothing
 * failed.
 *
 * The rules, in the order they apply:
 *
 * - Clearing the validation type removes every key of both modes.
 * - Switching modes removes the other mode's discriminant, so a question never carries both.
 * - Each fixed-range comparator keeps only the bounds it uses.
 * - `'withinMargin'` measures against the linked meter, so it drops the offset and the two
 *   reference questions: leaving them hands the runtimes a question carrying the keys of two
 *   comparators with nothing to say which one wins.
 * - Every value other than `'withinMargin'` drops the margins, since they mean nothing elsewhere.
 *
 * Writes that are not comparator changes, such as typing into a margin field, fall through
 * untouched: an author mid-edit must not have their other fields cleared underneath them.
 */
export function applyNumberValidationChange<T extends object>(
  question: T,
  action: string,
  value: string,
): T {
  // Widened rather than constrained: callers hold `QuestionInterface`, which has no index
  // signature, and the alternative is every call site casting at the boundary instead of one
  // cast here.
  const changed = { ...question } as NumberValidationKeys;

  // A margin belongs to `withinMargin` and to nothing else. Writing one onto a question that does
  // not carry that comparator leaves a key the configuration contract says cannot exist, and it
  // was reachable: unlinking the meter deletes the comparator and both margins, and a panel that
  // had not re-derived still offered the margin fields. The panel is keyed on the link now, so
  // this is the same rule enforced where the write happens rather than only where it is rendered.
  const isMarginWrite = action === 'lowerMargin' || action === 'upperMargin';
  if (isMarginWrite && changed.numberValidationRelative !== 'withinMargin') return question;

  changed[action] = value;

  if (action !== 'numberValidation' && action !== 'numberValidationRelative') return changed as T;

  if (value === '') {
    delete changed.numberValidation;
    delete changed.numberValidationRelative;
    delete changed.validationOffset;
    delete changed.upperValue;
    delete changed.lowerValue;
  }
  if (action === 'numberValidationRelative') {
    delete changed.numberValidation;
  }
  if (action === 'numberValidation') {
    delete changed.numberValidationRelative;
    delete changed.validationOffset;
  }
  if (value === 'greater' || value === 'greaterOrEqual') {
    delete changed.upperValue;
    changed.lowerValue = changed.lowerValue || '';
  }
  if (value === 'less' || value === 'lessOrEqual') {
    delete changed.lowerValue;
    changed.upperValue = changed.upperValue || '';
  }
  if (value === 'between' || value === 'notBetween') {
    delete changed.validationOffset;
    changed.lowerValue = changed.lowerValue || '';
    changed.upperValue = changed.upperValue || '';
  }
  if (value === 'noValidation') {
    delete changed.upperValue;
    delete changed.lowerValue;
    delete changed.numberValidation;
    delete changed.numberValidationRelative;
    delete changed.validationOffset;
  }
  if (value === 'withinMargin') {
    delete changed.validationOffset;
    delete changed.lowerValue;
    delete changed.upperValue;
  }
  if (value !== 'withinMargin') {
    delete changed.lowerMargin;
    delete changed.upperMargin;
  }

  return changed as T;
}

/** A question list as the old builder shape holds it: sub-questions nested under `questions`. */
/**
 * The minimum a question has to look like for these helpers to walk the list: an id, and for a
 * repeatable group, its children.
 *
 * No index signature. It looked harmless and it silently voided the constraint: `QuestionInterface`
 * is an interface, so it gets no implicit index signature, and passing `QuestionInterface[]` to a
 * `T extends QuestionListEntry` parameter fails with TS2345. The existing call sites compiled only
 * because the form-builder state is inferred as `never[]`, and the tests reached the helpers
 * through `as never` - so the constraint was checking nothing anywhere it mattered.
 */
interface QuestionListEntry {
  id: string;
  questions?: QuestionListEntry[];
}

/**
 * Applies the change to one top-level question, leaving the rest of the list untouched.
 */
export function applyNumberValidationChangeToQuestions<T extends QuestionListEntry>(
  questions: T[],
  id: string,
  action: string,
  value: string,
): T[] {
  return questions.map((question) =>
    question.id === id ? applyNumberValidationChange(question, action, value) : question,
  );
}

/**
 * Same, for a sub-question inside a repeatable group.
 *
 * Routing lives here rather than inline in the handler so both paths are reachable from a test.
 * The two inline copies were what let the table handler drift: it lacked the mutual-exclusion
 * deletes for a while, so the same edit left different keys behind depending on where the
 * question lived, and nothing failed.
 */
export function applyNumberValidationChangeInTable<T extends QuestionListEntry>(
  questions: T[],
  id: string,
  action: string,
  value: string,
  tableId: string,
): T[] {
  return questions.map((question) => {
    if (question.id !== tableId) return question;
    // A match with no children is left exactly as it is. `?? []` would have written
    // `questions: []` onto it instead, turning what used to be a loud failure into a silent
    // mutation of a question this function was never meant to touch. Not reachable from the
    // current call site, which always passes a real group id, but the quiet direction is the
    // expensive one to discover later.
    if (!question.questions) return question;
    return {
      ...question,
      questions: question.questions.map((subQuestion) =>
        subQuestion.id === id
          ? applyNumberValidationChange(subQuestion, action, value)
          : subQuestion,
      ),
    };
  });
}
